import classnames from "classnames";
import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import style from "../../scss/export.module.scss";
import { ChevronDownIcon, ChevronUpIcon, TriangleExclamationIcon } from "../Icons";
import { TSelectOption } from "../Select";
import "./dropdown.scss";

export type TDropdown = {
    children?: React.ReactNode;
    options?: Array<TSelectOption>;
    onSelectCallback?: ((selected: TSelectOption) => void) | null;
    onOpenCallback?: () => void;
    onCloseCallback?: () => void;
    fullWidth?: boolean;
    label?: string;
    labelClassName?: string;
    rightAlign?: boolean;
    value?: string;
    displayName?: string;
    onDarkBackground?: boolean;
    renderCustomButton?: (arg0: boolean, arg1: () => void, arg2: string) => any;
    renderValue?: (arg0: string | number) => JSX.Element | string | number;
    renderOption?: (arg0: TSelectOption) => JSX.Element | string | number;
    isValid?: boolean;
    errorMessage?: string;
    allowCustomColors?: boolean;
    disabled?: boolean;
};

export type TDropDownHandle = {
    setRefIsOpen: () => void;
};

export const Dropdown = forwardRef<TDropDownHandle, TDropdown>(
    (
        {
            label,
            options = [],
            onSelectCallback = null,
            children,
            fullWidth = false,
            labelClassName,
            rightAlign = false,
            value,
            onOpenCallback,
            onCloseCallback,
            onDarkBackground = false,
            renderCustomButton,
            renderOption,
            renderValue,
            isValid = true,
            errorMessage,
            // allowCustomColors = true,
            disabled = false,
        }: TDropdown,
        ref
    ) => {
        const [isOpen, setIsOpen] = useState(false);
        const wrapperRef = useRef(null);
        const dropdownId = useId();

        useEffect(() => {
            if (isOpen) {
                onOpenCallback?.();
            }
            // eslint-disable-next-line
        }, [isOpen]);

        useOutsideClick(isOpen, wrapperRef, () => {
            setIsOpen(false);
            onCloseCallback?.();
        });

        useImperativeHandle(ref, () => ({
            setRefIsOpen() {
                setIsOpen(false);
                onCloseCallback?.();
            },
        }));

        const labelId = useId();

        const onToggle = () => {
            setIsOpen(isOpen => !isOpen);
        };

        const onSelect = (event: React.MouseEvent<HTMLElement>, selected: TSelectOption) => {
            event.stopPropagation();
            onSelectCallback && onSelectCallback(selected);
            setIsOpen(false);
        };

        const onKeyDownSelect = (event: React.KeyboardEvent<HTMLElement>, selected: TSelectOption) => {
            if (event.key === "Enter" || event.key === " ") {
                onSelectCallback && onSelectCallback(selected);
                setIsOpen(false);
            }
        };

        const classNameDropdownWrapper = classnames("c-dropdown", {
            "c-dropdown--full-width": fullWidth,
            // "c-dropdown--custom-colors": allowCustomColors,
        });

        const classNameDropDownContent = classnames(
            "c-dropdown__content",
            !isOpen && "c-dropdown__content--hidden",
            rightAlign && "c-dropdown__content--right-align"
        );

        const classNameDropDown = classnames("c-dropdown__btn", { "c-dropdown__btn--error": !isValid });

        const classNameLabel = classnames("c-dropdown__label", labelClassName, {
            "c-dropdown__label--on-dark-bg": onDarkBackground,
        });

        const classNameErrorMessage = classnames("c-dropdown__errorMessageText");

        return (
            <div className={classNameDropdownWrapper} ref={wrapperRef}>
                {label && (
                    <label id={labelId} className={classNameLabel}>
                        {label}
                    </label>
                )}
                <div className="c-dropdown__content-wrapper">
                    <>
                        {renderCustomButton ? (
                            renderCustomButton(isOpen, onToggle, dropdownId)
                        ) : (
                            <button
                                className={classNameDropDown}
                                onClick={onToggle}
                                type="button"
                                disabled={disabled}
                                aria-haspopup="menu"
                                aria-expanded={isOpen}
                                aria-labelledby={label ? labelId : undefined}
                                aria-controls={dropdownId}
                            >
                                <div className="c-dropdown__btn-text">{(value && renderValue?.(value)) ?? value}</div>
                                {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </button>
                        )}
                        <div id={dropdownId} role="menu" className={classNameDropDownContent} aria-hidden={!isOpen}>
                            {children || (
                                <ul>
                                    {options.map((option: TSelectOption) => (
                                        <li
                                            key={option.value}
                                            role="menuitem"
                                            className={classnames("c-dropdown__option", option?.className, {
                                                "c-dropdown__option--disabled": option.disabled,
                                            })}
                                            onClick={e => onSelect(e, option)}
                                            onKeyDown={e => onKeyDownSelect(e, option)}
                                            tabIndex={0}
                                        >
                                            {renderOption?.(option) || option.label || option.value}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {!isValid && errorMessage && (
                            <div className="u-d-flex u-mt-6">
                                <TriangleExclamationIcon
                                    color={onDarkBackground ? style.whiteColor : style.alertColor}
                                />
                                <p
                                    className={classNameErrorMessage}
                                    style={{ color: onDarkBackground ? style.whiteColor : style.alertColor }}
                                >
                                    {errorMessage}
                                </p>
                            </div>
                        )}
                    </>
                </div>
            </div>
        );
    }
);

Dropdown.displayName = "Dropdown";
